// Atlas — the glossary.
//
// ONE PLACE FOR EVERY DEFINITION ON THE PAGE. A figure on a console gets either
// believed blindly or ignored, and both are worse than a figure with its
// meaning attached; the device ledger and Company revenue both learned this the
// expensive way. So every term this page shows is defined here, once, and the
// component reads from this file rather than restating anything in JSX.
//
// EACH ENTRY CARRIES THREE THINGS, and the third is the one that matters:
//   what   the plain-English definition
//   from   the table or derivation it comes from — so a reader can go and check
//   catch  the thing that would mislead someone who only read `what`. Several
//          of these record a figure that was WRONG on this page until recently
//          and say what it was asserting; that history is the most useful thing
//          a reader can know about a number already corrected once.
//
// Terms are keyed, never matched on display text, so re-wording a label cannot
// silently detach it from its definition.

export const GLOSSARY = {
  /* ── masthead ─────────────────────────────────────────────────────── */
  fittedToday: {
    term: 'Fitted today',
    what: 'Screens currently installed in a registered vehicle.',
    from: 'An active device_vehicles binding, joined to the vehicle and the inventory node.',
    catch: 'This is what is IN a cab, not what is working. It has read 16 since 10 August — the day '
         + 'the ten Pune bindings were written — and did not move even when a screen was swapped on '
         + '20 August, while the number that actually delivers has fallen by half.',
  },
  ranToday: {
    term: 'Ran today',
    what: 'Screens that delivered at least one ad today.',
    from: 'Distinct CAB-BOUND devices on impression_batches for the current IST day, inside each '
        + 'screen\'s own binding window, Mumbai and Pune only.',
    catch: 'Today is still in progress, so this climbs through the day. Compare it with yesterday, '
         + 'not with "fitted". A screen delivering before or after its binding is not counted here.',
  },
  fittedSilent: {
    term: 'Fitted and silent',
    what: 'Screens that were in a cab and delivered nothing.',
    from: 'Fitted minus ran, for the same day.',
    catch: 'The single number this page exists to surface. Two Mumbai cabs have been dark for four '
         + 'weeks and a third for three — one of them the first cab ever fitted. There is no reason '
         + 'code for a dark screen anywhere in the schema, so the data cannot say why.',
  },
  bestDayEver: {
    term: 'Best day ever',
    what: 'The most cab-bound screens that have delivered on one IST day.',
    from: 'The peak daily count of screens inside a live binding window, over the window this page '
        + 'covers — from 1 July 2026.',
    catch: 'Screens outside a vehicle binding — the bench television, the office tablets — are not '
         + 'in it. Set on Thu 13 Aug 2026 with all 16 running, and not matched since.',
  },
  measureSwitch: {
    term: 'The measure switch',
    what: 'Chooses which single figure each day card shows beneath its screen count.',
    from: 'Revenue is presence money; the rest come from signals.screen_context_hourly.',
    catch: 'Only the DAY cards change. The month card carries every measure at once, because it '
         + 'has a whole column of height and a 90px day card does not.',
  },

  /* ── the tape ─────────────────────────────────────────────────────── */
  tape: {
    term: 'Every cab, every day',
    what: 'One row per cab, one column per day. A cab keeps its row for the life of the record.',
    from: 'impression_batches per (device, IST day), UNGATED — the binding window is applied only '
        + 'when the cell is painted, which is what lets a pre-binding delivery show at all.',
    catch: 'Position is identity. A daily total cannot say WHICH screen stopped — three dark cabs '
         + 'and one dark cab both read there as "fewer today". Here weeks of silence is a stripe in '
         + 'the same place.',
  },
  tapeDelivered: {
    term: 'Delivered',
    what: 'That cab delivered at least one ad that day.',
    from: 'impression_batches.',
  },
  tapeSilent: {
    term: 'Fitted and silent',
    what: 'The screen was in the cab that day and delivered nothing.',
    from: 'Inside the binding window, no batches.',
  },
  tapeLag: {
    term: 'Delivered before the registry had it fitted',
    what: 'The screen was already delivering before anyone wrote its binding down.',
    from: 'A batch dated before its own device_vehicles.assigned_at.',
    catch: 'All ten Pune bindings were recorded on 10 Aug, but only ONE screen was delivering as '
         + 'early as the 5th; eight of the ten first appear on the 7th and one never delivered '
         + 'before its binding at all. It is not only Pune — two Mumbai screens ran days before '
         + 'theirs too. The money earned in those windows sits outside this page\'s totals.',
  },
  tapeUnfitted: {
    term: 'Not yet fitted',
    what: 'The screen was not in that cab on that day.',
    from: 'Outside the binding window.',
  },
  tapeMonths: {
    term: 'The figures on the left',
    what: 'That cab\'s presence-verified earnings, month by month.',
    from: 'The same rupees the day cards carry, cut by screen instead of by day.',
    catch: 'They reconcile with the daily series to the paise — that is the only reason this '
         + 'column is allowed to sit beside it.',
  },
  tapeDark: {
    term: 'Days since last delivered',
    what: 'How long that cab has been silent, in whole IST days.',
    from: 'Today minus the cab\'s last delivering day, differenced in the database.',
    catch: 'Turns red past a fortnight. Computed in SQL, not JS — doing it against a UTC-midnight '
         + 'date and a live clock returned −1 for a cab that ran today.',
  },

  /* ── month card ───────────────────────────────────────────────────── */
  monthRevenue: {
    term: 'The month figure',
    what: 'Presence-verified revenue for the month — money from plays with a passenger aboard.',
    from: 'signals.retro_presence_screen_hourly, which replays the gate over frozen history, up to '
        + 'the cutover at 07:30 IST on 28 Aug 2026; live presence verdicts from that instant on. '
        + 'The two sources split that day between them.',
    catch: 'Blanket pre-gate money appears NOWHERE on this page. August was billed about ₹1.5 lakh; '
         + 'the presence-verified slice is ₹44,000.',
  },
  screensFitted: {
    term: 'Screens fitted',
    what: 'Screens in a cab at the start of the month and at the end of it.',
    from: 'device_vehicles binding windows evaluated on the first and last IST day.',
  },
  typicallyLive: {
    term: 'Typically live',
    what: 'The average number of screens delivering on a day the network was serving.',
    from: 'Mean of the daily delivering count, over days with at least one fitted screen.',
    catch: 'Days before the network\'s first ad are excluded. Averaging them in would drag July '
         + 'toward zero and say something untrue about the screens that were running.',
  },
  ranPct: {
    term: 'Ran',
    what: 'The share of fitted screen-days that actually delivered.',
    from: 'Delivering screen-days over fitted screen-days, across days the network was serving.',
    catch: 'The denominator is what was in a cab, not what the company owns. 96% in July, 76% in '
         + 'August, 57% in September.',
  },
  perScreenDay: {
    term: 'Per screen-day',
    what: 'Presence revenue divided by the screen-days that DELIVERED an ad — not by the ones that '
        + 'earned.',
    from: 'Month revenue over delivering screen-days.',
    catch: 'Those are different counts and both are on this screen: in September 172 screen-days '
         + 'delivered and only 141 earned presence money, which is why this reads lower than the '
         + 'potential panel\'s per-cab-day figure. Yield on a working day has fallen every month — '
         + '₹182, ₹143, and ₹116 so far in September, still moving as today runs.',
  },
  plays: {
    term: 'Plays',
    what: 'Creative plays delivered, summed from impression_count.',
    from: 'impression_batches, cab-bound screens only.',
    catch: 'Not people and not impressions sold. A loop can repeat one creative hundreds of times '
         + 'in a single ride.',
  },
  distance: {
    term: 'Distance',
    what: 'Kilometres driven by the cabs while their screen was fitted.',
    from: 'signals.screen_context_hourly.distance_m, gated on the binding window.',
  },
  trips: {
    term: 'Trips',
    what: 'Driving legs completed by those cabs.',
    from: 'signals.screen_context_hourly.trips — motion segments of kind trip, attributed to the '
        + 'hour each one started.',
    catch: 'MOTION ONLY. A trip is a leg the cab drove, occupied or not, and presence plays no part '
         + 'in it. The presence-derived count is a different column this page does not show. There '
         + 'is no ride-hailing booking feed anywhere in this database, so this is not paid bookings.',
  },
  paxHours: {
    term: 'Passenger hrs',
    what: 'Hours with a passenger detected aboard.',
    from: 'signals.screen_context_hourly.presence_s.',
    catch: 'Compare with driving hours: the cabs carry someone about 40% of the time they move. '
         + 'That ratio, not screen uptime, is what caps sellable inventory.',
  },
  driveHours: {
    term: 'Driving hrs',
    what: 'Hours the cab was moving.',
    from: 'signals.screen_context_hourly.driving_s.',
  },
  metGateMonth: {
    term: 'Met the gate',
    what: 'Cab-days that cleared the day gate, of those the sweep has scored.',
    from: 'Frozen verdicts in device_day_eligibility — the same rows the payout plane pays on.',
    catch: 'Read, never recomputed. "Of N scored" matters: the sweep runs a day behind, and an '
         + 'unscored day is not a day nothing qualified.',
  },
  earnedByThose: {
    term: 'Earned by those',
    what: 'Presence revenue attributable to the cab-days that met the gate.',
    from: 'Presence money joined to the passing verdicts.',
    catch: 'A third of July and September, two fifths of August — the percentage beside it is the '
         + 'month\'s own figure. Of the rest, the part that was scored and failed reaches no one on '
         + 'the supply side; the not-yet-scored part has no verdict either way.',
  },
  gateRule: {
    term: 'The gate rule',
    what: 'The test a cab-day must pass for its money to be shareable.',
    from: 'The dated gate_profile on the premise-operator agreement, read per day — never a '
        + 'constant in code.',
    catch: 'It CHANGED. To 1 Aug 2026: 4 trips AND 5 driving hours. From 1 Aug: 4 trips AND 4 '
         + 'passenger hours. Each day is scored by the rule that governed it, so July and August '
         + 'cards name different rules. In practice passenger hours is what binds — of the failing '
         + 'device-days, 281 had four or more trips and fell short on hours; only 20 the reverse.',
  },
  momPct: {
    term: 'On last month',
    what: 'Change in presence revenue against the previous month.',
    catch: 'For a part month the raw figure compares a month-to-date total with a full month, '
         + 'which is not a comparison. September reads −55% raw and −26% per day like for like; '
         + 'both are shown, the honest one underneath.',
  },
  partMonth: {
    term: 'Part month',
    what: 'The month is still running, so its totals are incomplete.',
  },

  /* ── potential ────────────────────────────────────────────────────── */
  potential: {
    term: 'What it could be',
    what: 'What the same cabs would have earned at per-cab-day rates drawn from this fleet\'s own '
        + 'record.',
    catch: 'A CEILING, not a forecast. The best-day rungs are rates a cab really hit — the all-time '
         + 'one, ₹577, on Sun 19 Jul when exactly TWO screens were fitted. The percentile rungs are '
         + 'INTERPOLATED and may be no cab\'s actual day. And no cab has ever earned every day.',
  },
  potActual: {
    term: 'Actual',
    what: 'What the month really earned, and what that works out to per cab-day that EARNED.',
    catch: 'The divisor is cab-days carrying presence money, not cab-days that ran. Thirty-one '
         + 'September screen-days delivered ads and earned nothing; they are not in it.',
  },
  potRan: {
    term: '× ran',
    what: 'The rate applied to the cab-days that EARNED presence money.',
    catch: 'Not the same count as "Ran" on the month card, which counts screen-days that DELIVERED '
         + '— 141 against 172 in September. This column holds utilisation exactly where it is and '
         + 'moves yield alone.',
  },
  potFitted: {
    term: '× fitted',
    what: 'The rate applied to every cab-day a screen was in a cab, working or not.',
    catch: 'In August and September the gap between the columns is fleet-time that earned nothing. '
         + 'In July it is mostly the 31 cab-days BEFORE the network served its first ad — which the '
         + 'rest of this page is careful to call waiting rather than failing.',
  },
  potMedian: {
    term: 'The median cab-day',
    what: 'The earning level half the month\'s cab-days fall below.',
    from: 'percentile_cont(0.5) over presence money per (cab, day).',
    catch: 'percentile_cont INTERPOLATES, so this need not be any cab\'s actual day. September\'s '
         + 'ran-basis median is 1.0× actual — the typical working day is already earning the typical '
         + 'amount, so this rung offers nothing. The slack is above it: the 75th is still 1.5×.',
  },
  potP75: {
    term: 'The 75th percentile',
    what: 'The earning level three quarters of the month\'s cab-days fall below.',
    catch: 'Interpolated, so on a month whose count does not divide cleanly this is a point between '
         + 'two real cab-days rather than one of them. It is the honest improvement target.',
  },
  potBest: {
    term: 'Best day',
    what: 'The single highest-earning cab-day, this month and across the whole record.',
    catch: 'One cab on one day, and the all-time best (₹577, Sun 19 Jul) was set on a two-screen '
         + 'fleet. Multiplying it across sixteen cabs is arithmetic, not a plan.',
  },
  levers: {
    term: 'Yield · Utilisation · Both',
    what: 'The two ways the month could have been larger, sized against each other.',
    catch: 'Yield alone means better days; utilisation alone means more days. Which is bigger has '
         + 'moved — they were close in July and August, and in September utilisation pulled ahead. '
         + 'Getting dark cabs back on the road is now worth more than improving a working day.',
  },

  /* ── economics ────────────────────────────────────────────────────── */
  economics: {
    term: 'Where it goes',
    what: 'Every rupee of the month\'s presence revenue, followed to whoever ends up with it.',
    from: 'Rates read from the dated rows on revenue_share_agreements, split by the frozen gate '
        + 'verdicts and by ownership.',
    catch: 'NOT the settled share ledger, which works off invoiced base across every screen and '
         + 'both billing regimes and is larger. This is the same arithmetic on the money this page '
         + 'shows, which is what makes it reconcile with the cards beside it.',
  },
  splitBar: {
    term: 'supply · partners · pDOOH.ai',
    what: 'How the month\'s presence revenue divides between the three, before any number is read. '
        + 'Each slice is a share of gross.',
    catch: 'The supply slice is supply AFTER SIM recovery, not the "Supply share" row below. In '
         + 'September that is ₹87 against a share of ₹2,807, so the bar reads 0% while the row '
         + 'reads fourteen per cent of gross.',
  },
  technology: {
    term: 'Technology',
    what: 'The platform\'s first cut, taken off gross before anything is shared.',
    from: 'platform_tech on revenue_share_agreements, 2000 bps of GROSS.',
    catch: 'Basis is gross, unlike every other role, which take from the pool. On the agreement the '
         + 'other three rates sum to 100% of the pool; on this panel they sum to the SHAREABLE pool, '
         + 'because the gate and ownership come out first.',
  },
  pool: {
    term: 'Pool',
    what: 'What remains after technology — 80% of the month\'s presence revenue.',
    catch: 'The sharing rates below are NOT taken of this figure. They are taken of the part of the '
         + 'pool that both cleared the gate AND sits on a screen with a registered owner — shown as '
         + '"Shareable" two rows down. In August that was ₹13,310 of a ₹35,200 pool.',
  },
  metGateEcon: {
    term: 'Met the gate',
    what: 'The part of the pool sitting on cab-days that cleared the gate.',
    catch: 'Only this part can be shared, and not all of it — the booked-house row comes out first, '
         + 'because a screen with no owner on record is paid to nobody. In August ₹14,404 cleared '
         + 'the gate and ₹13,310 of it was shareable.',
  },
  noOwner: {
    term: 'No owner on record — booked house',
    what: 'A qualified cab-day on a screen with no registered owner. It is shareable in principle '
        + 'and paid to nobody.',
    from: 'ownership_history resolved on the edge node, the same rule the payout engine uses.',
    catch: 'Being bolted into a cab does not make a screen somebody\'s to be paid for. This page '
         + 'had no concept of ownership until 19 Sep and reported ₹601.62 of August as owed to the '
         + 'fleet partner for a screen nobody is owed.',
  },
  shareablePool: {
    term: 'Shareable',
    what: 'The only part of the pool that can be paid to anyone: cleared the gate AND has an owner '
        + 'on record.',
    catch: 'The three rates beneath sum to exactly this. It is printed as its own line because '
         + 'without it those rows summed to 38% of the Pool figure above them and nobody could '
         + 'reproduce the arithmetic.',
  },
  premiseOwner: {
    term: 'Premise owner',
    what: 'The party that owns the cab the screen rides in.',
    from: 'premise_operator on revenue_share_agreements, 3000 bps — of the shareable pool.',
  },
  deviceOwner: {
    term: 'Device owner',
    what: 'The party that owns the screen itself.',
    from: 'device_owner on revenue_share_agreements, 2500 bps — of the shareable pool.',
    catch: 'CabE holds both roles for every screen it owns, so premise and device go to the same '
         + 'party — 55% of the shareable pool between them. As a share of the month\'s gross that '
         + 'came to 11.5% in July, 16.6% in August and 14.1% in September, because most of the pool '
         + 'never becomes shareable.',
  },
  platformMargin: {
    term: 'pDOOH.ai (in the pool)',
    what: 'The platform\'s rate share of the shareable pool.',
    from: 'platform_margin on revenue_share_agreements, 4500 bps of the shareable pool.',
    catch: 'This row is NOT everything the platform takes from the gate-passing pool. The '
         + 'booked-house carve above is added to it before "pDOOH.ai take" further down, so that '
         + 'total is larger than this row by exactly the no-owner amount.',
  },
  didNotMeet: {
    term: 'Did not meet the gate',
    what: 'What is left of the pool once the gate-passing and not-yet-scored parts are taken out: '
        + 'cab-days that were scored and failed. The advertiser still paid; supply gets none of it.',
    catch: 'The gate working, not revenue lost — the same rule redrafted the fleet partner\'s August '
         + 'statement from ₹30,667 to ₹8,279. It is a residual, so anything the day-level unscored '
         + 'line does not catch lands here.',
  },
  notScored: {
    term: 'Not scored yet',
    what: 'Pool on days the eligibility sweep has not reached. No verdict exists either way.',
    catch: 'A third state, not a failure, and it is subtracted out before the failed bucket so '
         + 'unscored money is never asserted to have failed. The sweep runs at 09:00 IST and scores '
         + 'the previous day, so today is always open and yesterday is open until the morning run.',
  },
  supplyShare: {
    term: 'Supply share',
    what: 'Premise plus device share on the owned, gate-passing pool — before expenses.',
  },
  simRecovery: {
    term: 'SIM recovery',
    what: 'Connectivity cost recovered from the supply side.',
    from: 'expense_config on the premise-operator agreement — ₹170 per device-month, multiplied by '
        + 'the screens fitted on the last day shown.',
    catch: 'UNGATED — charged per fitted device whether or not it earned, and charged IN FULL on a '
         + 'part month: September deducts all ₹2,720 against nineteen days of earnings. At present '
         + 'pass rates it consumes nearly the whole share.',
  },
  toSupplyNet: {
    term: 'To supply, net',
    what: 'What the supply side is left with after SIM recovery.',
    catch: 'Not floored at zero, and not what the operator is actually paid. The real close '
         + 'withholds any positive net below ₹1,000 and carries it forward — which is what July\'s '
         + '₹25 and September\'s ₹87 would do — and a negative net carries in full.',
  },
  pdoohTake: {
    term: 'pDOOH.ai take',
    what: 'Technology, the platform\'s rate share, the gate-passing pool on screens with no owner '
        + 'on record, everything the gate withheld, anything unscored, and the SIM recovered.',
    catch: 'Every rupee that is not "to supply, net" is in here, the booked-house row included — '
         + 'which is why this is larger than the pDOOH.ai rate row above by exactly that amount.',
  },
  partners: {
    term: 'Partners',
    what: '15% set aside for partners.',
    catch: 'NOTIONAL: not a term on any agreement, in no ledger — an internal planning figure. Note '
         + 'the basis: the founder set it against TOTAL ad revenue, and this panel applies it to the '
         + 'PRESENCE revenue shown here, which is smaller. It is absorbed out of pDOOH\'s take, so '
         + 'it never reduces what supply is paid — and in September it exceeds the whole supply share.',
  },
  pdoohNet: {
    term: 'pDOOH.ai net',
    what: 'What the platform keeps once partners are carved out.',
  },

  monthPeak: {
    term: 'Best day (month card)',
    what: 'The most screens live on one day that month — a COUNT of screens, not an amount earned.',
    catch: 'Do not confuse it with the best cab-day in the potential column beside it, which is a '
         + 'rupee figure for one cab on one day. Different quantities, adjacent on screen.',
  },

  dayStates: {
    term: 'The three day-card colours',
    what: 'Teal — every fitted screen ran. Plain — some ran. Rose — fitted and none ran.',
    catch: 'A fourth, dashed state means the screen was fitted before the network began serving: '
         + 'waiting, not failing. Marking those as dark would have put seventeen red days in July '
         + 'and buried the September shortfall.',
  },

  daySpread: {
    term: 'N cabs earned · best · median',
    what: 'How that day\'s presence money was spread across the cabs that earned any.',
    catch: 'The top cab routinely takes 20–40% of a day on its own. A fleet average would hide that, '
         + 'which is why the best and the median are shown rather than the mean.',
  },

  panelFitted: {
    term: 'Screens fitted · Screens that ran (day panel)',
    what: 'For the held day: screens in a cab, and screens that delivered.',
    catch: 'Both are for that single day. The month card\'s "Screens fitted" shows the month\'s '
         + 'first and last day instead.',
  },

  /* ── week + day ───────────────────────────────────────────────────── */
  weekCell: {
    term: 'The week row',
    what: 'The same measures as the month, for the seven days beside it.',
    catch: 'It is a cell of the same grid as those days, not a panel next to them, so the two '
         + 'cannot drift apart.',
  },
  dayCount: {
    term: 'n / N on a day card',
    what: 'Screens that delivered that day, over screens that were fitted.',
  },
  dayPct: {
    term: 'The percentage',
    what: 'Share of fitted screens that ran that day.',
  },
  dayAhead: {
    term: 'AHEAD',
    what: 'A screen delivered that day BEFORE the registry recorded it as fitted.',
    from: 'A batch dated earlier than its own device_vehicles.assigned_at.',
    catch: 'Not "more ran than were fitted" — every count on this page is gated on the binding '
         + 'window, so that can never happen. The registry is what is wrong on these days, not the '
         + 'delivery, and the money earned then sits outside this page\'s totals.',
  },
  dayCities: {
    term: 'M · P',
    what: 'Mumbai and Pune: screens that ran over screens fitted, in each city.',
    catch: 'The balance has MOVED. Pune is 60% of plays across this whole record, 59% in August, '
         + '86% in September — and in the last week, with Mumbai nearly dark, 97%. "Mumbai and Pune" '
         + 'is true and is not the same claim as a two-city network.',
  },
  dayGate: {
    term: 'GATE',
    what: 'Cabs that met the day gate, of those scored — and what they earned.',
    from: 'Frozen verdicts from device_day_eligibility, joined to presence money.',
    catch: 'The figure beside it is the shareable half of the day. "Not scored" is not zero.',
  },
  dayMilestone: {
    term: '+N on a day card',
    what: 'Screens fitted or removed that day.',
  },
  preService: {
    term: 'Fitted · not serving',
    what: 'The screen was in a cab before the network had delivered its first ad.',
    catch: 'Waiting, not failing. Marking these as dark would have put seventeen red days in July '
         + 'and buried the September shortfall.',
  },
};

export const term = (key) => GLOSSARY[key] || null;
